/**
 * usePatrolLocations Hook
 *
 * Derives patrol location data from guards for rendering circles on the map.
 * Calculates guard-to-site distance and determines circle status.
 *
 * This is a derived/computed hook, not a data fetching hook. It transforms
 * guard data that's already fetched by useGuardLocations.
 */

import { useMemo } from 'react';
import {
  GuardLocation,
  PatrolLocation,
  PatrolCircleStatus,
  PATROL_CIRCLE_RADIUS_METERS,
} from '../../components/guardLocation/types';
import { calculateDistanceMeters } from '../../components/guardLocation/utils';

/** Result returned by usePatrolLocations hook */
export interface UsePatrolLocationsResult {
  /** Patrol locations with coordinates for rendering circles */
  patrolLocations: PatrolLocation[];
  /** Count of sites with coordinates */
  sitesWithCoordinates: number;
  /** Count of sites missing coordinates */
  sitesMissingCoordinates: number;
  /** Total sites (including those without coordinates) */
  totalSites: number;
}

/**
 * Determine the patrol circle status based on guard position and patrol status.
 */
function getPatrolCircleStatus(
  patrolStatus: 'pending' | 'in_progress' | 'completed',
  guardDistance: number | null,
  radiusMeters: number = PATROL_CIRCLE_RADIUS_METERS
): PatrolCircleStatus {
  // Completed patrols are always blue
  if (patrolStatus === 'completed') {
    return 'completed';
  }

  // No guard location available
  if (guardDistance === null) {
    return 'no_guard';
  }

  // Guard is inside the radius
  if (guardDistance <= radiusMeters) {
    return 'guard_inside';
  }

  // Guard is outside the radius
  return 'guard_outside';
}

/**
 * Hook to derive patrol locations from guard data.
 *
 * Transforms guards with currentPatrol.siteCoordinates into PatrolLocation
 * objects suitable for rendering circles on the map.
 *
 * @param guards - Array of guards from useGuardLocations
 * @returns Patrol locations with circle status and distance calculations
 */
export function usePatrolLocations(guards: GuardLocation[]): UsePatrolLocationsResult {
  return useMemo(() => {
    // Track sites by addressId to deduplicate (multiple guards at same site)
    const siteMap = new Map<number, PatrolLocation>();
    let sitesMissingCoordinates = 0;

    for (const guard of guards) {
      const patrol = guard.currentPatrol;

      // Skip guards not on patrol
      if (!patrol) {
        continue;
      }

      // Skip patrols without addressId
      if (!patrol.addressId) {
        sitesMissingCoordinates++;
        continue;
      }

      // Skip patrols without site coordinates
      if (!patrol.siteCoordinates) {
        sitesMissingCoordinates++;
        continue;
      }

      const { latitude: siteLat, longitude: siteLng } = patrol.siteCoordinates;

      // Calculate distance from guard to site center
      const guardDistance = calculateDistanceMeters(
        guard.latitude,
        guard.longitude,
        siteLat,
        siteLng
      );

      // Determine circle status
      const circleStatus = getPatrolCircleStatus(
        patrol.patrolStatus || 'pending',
        guardDistance
      );

      // Check if we already have this site (by addressId)
      const existingEntry = siteMap.get(patrol.addressId);

      if (existingEntry) {
        // If there's already an entry for this site, prefer the one with:
        // 1. Guard inside > Guard outside > No guard
        // 2. Shorter distance if both have guards
        const shouldReplace =
          (circleStatus === 'guard_inside' && existingEntry.circleStatus !== 'guard_inside') ||
          (circleStatus === 'guard_outside' &&
            existingEntry.circleStatus === 'no_guard') ||
          (existingEntry.guardDistance !== null &&
            guardDistance < existingEntry.guardDistance);

        if (shouldReplace) {
          siteMap.set(patrol.addressId, {
            patrolId: patrol.patrolId,
            addressId: patrol.addressId,
            siteName: patrol.siteName || patrol.customerName,
            customerName: patrol.customerName,
            latitude: siteLat,
            longitude: siteLng,
            circleStatus,
            guardId: guard.guardId,
            guardName: guard.name,
            guardDistance,
            patrolStatus: patrol.patrolStatus || 'pending',
          });
        }
      } else {
        // First guard at this site
        siteMap.set(patrol.addressId, {
          patrolId: patrol.patrolId,
          addressId: patrol.addressId,
          siteName: patrol.siteName || patrol.customerName,
          customerName: patrol.customerName,
          latitude: siteLat,
          longitude: siteLng,
          circleStatus,
          guardId: guard.guardId,
          guardName: guard.name,
          guardDistance,
          patrolStatus: patrol.patrolStatus || 'pending',
        });
      }
    }

    const patrolLocations = Array.from(siteMap.values());

    return {
      patrolLocations,
      sitesWithCoordinates: patrolLocations.length,
      sitesMissingCoordinates,
      totalSites: patrolLocations.length + sitesMissingCoordinates,
    };
  }, [guards]);
}
